/**
 * Phase 98ax — Validation Terrain (tablette, sur place au distributeur).
 * Route : /tournees-reappro/:id/terrain
 *
 * Workflow terrain :
 *   1. L'opérateur sélectionne un distributeur dans la tournée EN_LIVRAISON.
 *   2. Il scanne EAN13 chaque barquette qu'il charge → auto-incrémente "Chargé".
 *   3. Il saisit sur chaque ligne : le résiduel (contenu réel), les DLC retirées
 *      et éventuellement une note (bouton +/- géants tactiles).
 *   4. À la validation → POST /refill-tours/{id}/reconcile avec dlc_expired_qty
 *      populé → clôture + mouvements WASTE (DLC retirées + éventuel manquant).
 *
 * Différences vs RefillTourReconcile.jsx (admin) :
 *   - Interface tactile (gros boutons, un distrib à la fois)
 *   - Scanner EAN13 clavier + caméra intégré
 *   - Champ DLC retirées séparé (traçabilité HACCP sur place)
 *   - Highlight visuel sur la ligne scannée + toast produit
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ArrowLeft,
  CheckCircle2,
  Loader2,
  ScanLine,
  Camera,
  Plus,
  Minus,
  AlertCircle,
  Smartphone,
  ClipboardCheck,
  Undo2,
} from "lucide-react";
import { toast } from "sonner";
import BarcodeScanner from "../components/BarcodeScanner";
import { useConfirm } from "@/components/ConfirmDialog";

const lineKey = (l) => `${l.distributor_id}::${l.product_id}::${l.slot_number || ""}`;

export default function RefillTourOnSite() {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const scanRef = useRef(null);
  const [tour, setTour] = useState(null);
  const [selectedDistrib, setSelectedDistrib] = useState("");
  const [lineMap, setLineMap] = useState({});
  const [headerMap, setHeaderMap] = useState({});
  const [scanInput, setScanInput] = useState("");
  const [cameraOpen, setCameraOpen] = useState(false);
  const [lastScannedKey, setLastScannedKey] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // ------- Chargement initial -------
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/refill-tours/${id}`);
        setTour(data);
        const initLines = {};
        const initHeaders = {};
        for (const ln of data.lines || []) {
          const qty_shipped = Number(
            ln.qty_shipped || ln.qty_loaded || ln.qty_planned || 0
          );
          if (qty_shipped <= 0) continue;
          initLines[lineKey(ln)] = {
            contenu_reel: 0,
            reappro: 0,
            dlc_expired_qty: 0,
            note: "",
          };
          if (!initHeaders[ln.distributor_id]) {
            initHeaders[ln.distributor_id] = {
              heure_passage: "",
              nettoye: false,
              etat_machine: "",
              note_generale: "",
            };
          }
        }
        setLineMap(initLines);
        setHeaderMap(initHeaders);
        // Auto-sélection du premier distrib (ordonné selon selected_distributor_ids)
        const distribIds = (data.selected_distributor_ids || []).filter((did) =>
          (data.lines || []).some(
            (ln) =>
              ln.distributor_id === did &&
              Number(ln.qty_shipped || ln.qty_loaded || ln.qty_planned || 0) > 0
          )
        );
        if (distribIds.length && !selectedDistrib) {
          setSelectedDistrib(distribIds[0]);
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Erreur chargement tournée");
        navigate("/tournees-reappro");
      }
    })();
  }, [id]);

  // ------- Groupage par distributeur -------
  const distribGroups = useMemo(() => {
    if (!tour) return [];
    const grp = {};
    for (const ln of tour.lines || []) {
      const qty = Number(
        ln.qty_shipped || ln.qty_loaded || ln.qty_planned || 0
      );
      if (qty <= 0) continue;
      const did = ln.distributor_id;
      if (!grp[did]) {
        grp[did] = {
          distributor_id: did,
          distributor_name: ln.distributor_name,
          distributor_code: ln.distributor_code,
          distributor_city: ln.distributor_city,
          lines: [],
        };
      }
      grp[did].lines.push(ln);
    }
    const ordered = (tour.selected_distributor_ids || [])
      .map((did) => grp[did])
      .filter(Boolean);
    Object.keys(grp).forEach((did) => {
      if (!(tour.selected_distributor_ids || []).includes(did))
        ordered.push(grp[did]);
    });
    ordered.forEach((g) =>
      g.lines.sort((a, b) => {
        const sa = parseInt(String(a.slot_number || "9999").split(/[,/]/)[0], 10);
        const sb = parseInt(String(b.slot_number || "9999").split(/[,/]/)[0], 10);
        if (sa !== sb) return sa - sb;
        return (a.product_name || "").localeCompare(b.product_name || "");
      })
    );
    return ordered;
  }, [tour]);

  const currentGroup = useMemo(
    () => distribGroups.find((g) => g.distributor_id === selectedDistrib),
    [distribGroups, selectedDistrib]
  );

  // ------- Totaux (distrib courant) -------
  const totals = useMemo(() => {
    if (!currentGroup) return { planned: 0, reappro: 0, dlc: 0, missing: 0, extra: 0 };
    let planned = 0,
      reappro = 0,
      dlc = 0,
      missing = 0,
      extra = 0;
    for (const ln of currentGroup.lines) {
      const key = lineKey(ln);
      const r = lineMap[key] || {};
      const shipped = Number(
        ln.qty_shipped || ln.qty_loaded || ln.qty_planned || 0
      );
      const reapp = Number(r.reappro || 0);
      const d = shipped - reapp;
      planned += shipped;
      reappro += reapp;
      dlc += Number(r.dlc_expired_qty || 0);
      if (d > 0.001) missing += d;
      if (d < -0.001) extra += -d;
    }
    return {
      planned: Math.round(planned * 100) / 100,
      reappro: Math.round(reappro * 100) / 100,
      dlc: Math.round(dlc * 100) / 100,
      missing: Math.round(missing * 100) / 100,
      extra: Math.round(extra * 100) / 100,
    };
  }, [currentGroup, lineMap]);

  // ------- Update helpers -------
  const setLine = (l, patch) => {
    const key = lineKey(l);
    setLineMap((m) => ({ ...m, [key]: { ...(m[key] || {}), ...patch } }));
  };
  const bumpLine = (l, field, delta) => {
    const key = lineKey(l);
    setLineMap((m) => {
      const cur = Number((m[key] || {})[field] || 0);
      const next = Math.max(0, cur + delta);
      return { ...m, [key]: { ...(m[key] || {}), [field]: next } };
    });
  };
  const setHeader = (did, field, value) => {
    setHeaderMap((m) => ({ ...m, [did]: { ...(m[did] || {}), [field]: value } }));
  };

  // ------- Scan handling -------
  // Phase 101gf (Feb 2026) — Garde-fou re-focus : ne re-focus le scan QUE si
  // l'utilisateur n'est pas en train de taper dans un autre input (clavier
  // tablette doit pouvoir rester actif sur un champ quantité/note).
  const refocusScan = () => {
    setTimeout(() => {
      const active = document.activeElement;
      const isTyping = active && active !== scanRef.current && (
        active.tagName === "INPUT" || active.tagName === "TEXTAREA" || active.tagName === "SELECT"
      );
      if (!isTyping) scanRef.current?.focus();
    }, 50);
  };

  // Phase 101gf — Undo du dernier scan en 1 clic (annule le +1 précédent).
  const undoLastScan = () => {
    if (!lastScannedKey) {
      toast.info("Aucun scan à annuler.");
      return;
    }
    const group = distribGroups.find((g) => g.distributor_id === selectedDistrib);
    const target = (group?.lines || []).find((ln) => lineKey(ln) === lastScannedKey);
    if (!target) {
      toast.info("Ligne du dernier scan introuvable sur ce distributeur.");
      return;
    }
    const current = Number(lineMap[lastScannedKey]?.reappro || 0);
    if (current <= 0) {
      toast.info("Déjà à 0 — rien à annuler.");
      return;
    }
    bumpLine(target, "reappro", -1);
    toast.success("Dernier scan annulé (-1)");
  };

  const handleScan = async (e, overrideEan) => {
    if (e) e.preventDefault();
    const ean = (overrideEan ?? scanInput ?? "").trim();
    if (!ean) return;
    if (!selectedDistrib) {
      toast.warning("Sélectionnez d'abord un distributeur.");
      return;
    }
    // Phase 101gf — Vide l'input AVANT l'appel : si le scan échoue, le prochain
    // scan ne se concatène pas sur l'EAN resté bloqué.
    setScanInput("");
    try {
      const { data } = await api.post(`/refill-tours/${id}/lookup-ean`, {
        ean13: ean,
        distributor_id: selectedDistrib,
      });
      const productId = data.product?.id;
      const matches = data.matching_lines || [];
      if (matches.length === 0) {
        toast.warning(
          `${data.product?.name || "Produit"} : aucun slot prévu pour ce distributeur`
        );
        refocusScan();
        return;
      }
      // Trouve le/les line objects correspondants dans le groupe courant
      const group = distribGroups.find((g) => g.distributor_id === selectedDistrib);
      const targetLines = (group?.lines || []).filter(
        (ln) => ln.product_id === productId
      );
      if (targetLines.length === 1) {
        // Slot unique → auto-incrémente
        bumpLine(targetLines[0], "reappro", 1);
        setLastScannedKey(lineKey(targetLines[0]));
        toast.success(
          `${data.product.name} · slot ${targetLines[0].slot_number || "?"} +1`
        );
      } else {
        // Multi-slot : incrémente le premier slot pas encore à sa capacité
        const sorted = [...targetLines].sort((a, b) => {
          const ra = Number(lineMap[lineKey(a)]?.reappro || 0);
          const rb = Number(lineMap[lineKey(b)]?.reappro || 0);
          const shippedA = Number(
            a.qty_shipped || a.qty_loaded || a.qty_planned || 0
          );
          const shippedB = Number(
            b.qty_shipped || b.qty_loaded || b.qty_planned || 0
          );
          // Prend celui où reappro < shipped en priorité
          const remA = shippedA - ra;
          const remB = shippedB - rb;
          return remB - remA;
        });
        const target = sorted[0];
        bumpLine(target, "reappro", 1);
        setLastScannedKey(lineKey(target));
        toast.success(
          `${data.product.name} · slot ${target.slot_number || "?"} +1 (${targetLines.length} slots — vérifiez)`
        );
      }
      refocusScan();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Scan refusé: « ${ean} »`);
      refocusScan();
    }
  };

  const handleCameraScan = (decoded) => {
    if (!decoded) return;
    setCameraOpen(false);
    setScanInput(decoded);
    handleScan(null, decoded);
  };

  // ------- Soumission -------
  const submit = async () => {
    if (!tour) return;
    const lines = [];
    for (const g of distribGroups) {
      for (const ln of g.lines) {
        const key = lineKey(ln);
        const r = lineMap[key] || {};
        lines.push({
          distributor_id: ln.distributor_id,
          product_id: ln.product_id,
          slot_number: ln.slot_number || "",
          contenu_reel: Number(r.contenu_reel || 0),
          reappro: Number(r.reappro || 0),
          dlc_expired_qty: Number(r.dlc_expired_qty || 0),
          note: (r.note || "").trim(),
        });
      }
    }
    const headers = Object.entries(headerMap).map(([distributor_id, h]) => ({
      distributor_id,
      heure_passage: (h.heure_passage || "").trim(),
      nettoye: Boolean(h.nettoye),
      etat_machine: (h.etat_machine || "").trim(),
      note_generale: (h.note_generale || "").trim(),
    }));
    // Bilan global : cumul de dlc + écarts sur TOUS les distribs
    let dlcTotal = 0,
      missTotal = 0;
    for (const l of lines) {
      dlcTotal += Number(l.dlc_expired_qty || 0);
      const ln = (tour.lines || []).find(
        (x) =>
          x.distributor_id === l.distributor_id &&
          x.product_id === l.product_id &&
          (x.slot_number || "") === l.slot_number
      );
      const shipped = Number(
        ln?.qty_shipped || ln?.qty_loaded || ln?.qty_planned || 0
      );
      const delta = shipped - Number(l.reappro || 0);
      if (delta > 0.001) missTotal += delta;
    }
    const desc =
      dlcTotal + missTotal < 0.001
        ? "Aucune DLC ni écart à traiter. La tournée sera clôturée sans mouvement de stock supplémentaire."
        : `Mouvements générés :\n• ${dlcTotal.toFixed(2)} barquette(s) WASTE DLC retirées\n• ${missTotal.toFixed(2)} barquette(s) WASTE (écart Prévu vs Chargé)\n\nLa tournée passe en CLOTUREE.`;
    const ok = await confirm({
      title: "Clôturer la tournée depuis le terrain ?",
      description: desc,
      confirmText: "Valider & clôturer",
      destructive: dlcTotal + missTotal > 0.001,
    });
    if (!ok) return;

    setSubmitting(true);
    try {
      const { data } = await api.post(`/refill-tours/${id}/reconcile`, {
        lines,
        headers,
        operator_name: "",
      });
      const nbMv = (data.missing_movements || []).length;
      const nbW = (data.warnings || []).length;
      toast.success(
        `Tournée clôturée : ${data.reconciled_lines} slots · ${nbMv} mouvement(s) WASTE` +
          (nbW > 0 ? ` · ${nbW} avertissement(s)` : "")
      );
      if (data.warnings?.length) data.warnings.forEach((w) => toast.warning(w));
      navigate(`/tournees-reappro/${id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur clôture terrain");
    } finally {
      setSubmitting(false);
    }
  };

  // ------- Render -------
  if (!tour) {
    return (
      <div className="p-8 text-center">
        <Loader2 className="w-6 h-6 animate-spin inline" /> Chargement…
      </div>
    );
  }

  if (tour.status === "CLOTUREE") {
    return (
      <div className="p-8 text-center space-y-3" data-testid="tour-already-closed">
        <CheckCircle2 className="w-12 h-12 mx-auto text-[#1F4E3D]" />
        <p className="text-lg font-medium">Cette tournée est déjà clôturée.</p>
        <Button onClick={() => navigate(`/tournees-reappro/${id}`)}>Voir le détail</Button>
      </div>
    );
  }

  if (tour.status !== "EN_LIVRAISON") {
    return (
      <div className="p-8 text-center space-y-3">
        <AlertCircle className="w-12 h-12 mx-auto text-[#C05C47]" />
        <p className="text-lg font-medium">
          Validation terrain disponible uniquement après verrouillage (statut EN_LIVRAISON).
        </p>
        <p className="text-sm text-[#6B6E6B]">
          Statut actuel : <strong>{tour.status}</strong>
        </p>
        <Button onClick={() => navigate(`/tournees-reappro/${id}`)}>Retour au détail</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-3 sm:p-4 pb-24" data-testid="onsite-page">
      {/* Header page */}
      <div className="flex items-center gap-3 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/tournees-reappro/${id}`)}
          data-testid="onsite-back"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour
        </Button>
        <div className="flex items-center gap-2">
          <Smartphone className="w-5 h-5 text-[#1F4E3D]" />
          <h1 className="text-lg sm:text-xl font-bold font-mono">{tour.reference}</h1>
          <Badge className="bg-[#EAF1F8] text-[#2E5C8A] border-[#C5D9E8]">Validation Terrain</Badge>
        </div>
      </div>

      {/* Bandeau sticky : sélecteur distrib + scan */}
      <div
        className="sticky top-0 z-30 bg-white border border-[#E5E5E0] p-3 shadow-sm space-y-3"
        data-testid="onsite-sticky-bar"
      >
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[220px]">
            <div className="text-[10px] uppercase tracking-wider text-[#6B6E6B] mb-1">
              Distributeur
            </div>
            <select
              value={selectedDistrib}
              onChange={(e) => setSelectedDistrib(e.target.value)}
              className="w-full h-12 border border-[#E5E5E0] px-3 text-base font-mono focus:outline-none focus:border-[#1F4E3D]"
              data-testid="onsite-distrib-select"
            >
              <option value="">-- Choisir --</option>
              {distribGroups.map((g) => (
                <option key={g.distributor_id} value={g.distributor_id}>
                  {g.distributor_code} · {g.distributor_name}
                </option>
              ))}
            </select>
          </div>

          <form onSubmit={handleScan} className="flex items-end gap-2 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#6B6E6B] mb-1 flex items-center gap-1">
                <ScanLine className="w-3 h-3" /> Scan EAN13
              </div>
              <Input
                ref={scanRef}
                value={scanInput}
                onChange={(e) => setScanInput(e.target.value)}
                placeholder="Scannez ou tapez…"
                autoFocus
                inputMode="none"
                className="w-52 h-12 text-base font-mono"
                data-testid="onsite-scan-input"
                disabled={!selectedDistrib}
              />
            </div>
            <Button
              type="submit"
              className="h-12 bg-[#1F4E3D] hover:bg-[#173D2F]"
              disabled={!selectedDistrib}
              data-testid="onsite-scan-submit"
            >
              <ScanLine className="w-4 h-4 mr-1" /> +1
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-12 border-[#C05C47]/40 text-[#C05C47] hover:bg-[#C05C47]/5"
              onClick={undoLastScan}
              disabled={!lastScannedKey}
              data-testid="onsite-undo-scan"
              title="Annule le +1 du dernier scan"
            >
              <Undo2 className="w-4 h-4 mr-1" /> Annuler
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-12"
              onClick={() => {
                if (!selectedDistrib) {
                  toast.warning("Sélectionnez d'abord un distributeur.");
                  return;
                }
                setCameraOpen(true);
              }}
              data-testid="onsite-camera-btn"
              title="Scanner via la caméra"
            >
              <Camera className="w-4 h-4" />
            </Button>
          </form>
        </div>

        {/* Bilan Prévu vs Chargé pour le distrib courant */}
        {currentGroup && (
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 text-center">
            <Stat label="Prévu" value={totals.planned} color="#1A1C1A" />
            <Stat label="Chargé" value={totals.reappro} color="#1F4E3D" />
            <Stat
              label="Manque"
              value={totals.missing}
              color="#C05C47"
              warn={totals.missing > 0}
            />
            <Stat
              label="En +"
              value={totals.extra}
              color="#B48A00"
              warn={totals.extra > 0}
            />
            <Stat
              label="DLC ret."
              value={totals.dlc}
              color="#8C6B2F"
              warn={totals.dlc > 0}
            />
          </div>
        )}
      </div>

      {/* Empty state */}
      {!selectedDistrib && (
        <div
          className="p-6 text-center border-2 border-dashed border-[#E5E5E0] bg-[#FAFAF7]"
          data-testid="onsite-empty"
        >
          <ClipboardCheck className="w-8 h-8 mx-auto text-[#6B6E6B] mb-2" />
          <p className="text-[#575A57]">
            Sélectionnez un distributeur pour commencer la validation terrain.
          </p>
        </div>
      )}

      {/* Table lignes du distrib courant */}
      {currentGroup && (
        <div className="border border-[#E5E5E0] bg-white overflow-hidden">
          <div className="bg-[#1F4E3D] text-white px-4 py-2 flex items-center gap-2 flex-wrap">
            <span className="font-mono font-semibold">{currentGroup.distributor_code}</span>
            <span className="font-medium">{currentGroup.distributor_name}</span>
            {currentGroup.distributor_city && (
              <span className="text-xs opacity-80">· {currentGroup.distributor_city}</span>
            )}
            <div className="flex-1" />
            <span className="text-xs opacity-80">
              {currentGroup.lines.length} slot(s) à traiter
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAF7]">
                <tr>
                  <th className="text-left px-2 py-2 w-12">Slot</th>
                  <th className="text-left px-2 py-2">Produit</th>
                  <th className="text-center px-2 py-2 w-14">Prévu</th>
                  <th className="text-center px-2 py-2 w-24">Rest.</th>
                  <th className="text-center px-2 py-2 w-28">DLC ret.</th>
                  <th className="text-center px-2 py-2 w-36">Chargé</th>
                  <th className="text-center px-2 py-2 w-14">Écart</th>
                  <th className="text-left px-2 py-2 min-w-[110px]">Note</th>
                </tr>
              </thead>
              <tbody>
                {currentGroup.lines.map((ln) => {
                  const key = lineKey(ln);
                  const r = lineMap[key] || {
                    contenu_reel: 0,
                    reappro: 0,
                    dlc_expired_qty: 0,
                    note: "",
                  };
                  const shipped = Number(
                    ln.qty_shipped || ln.qty_loaded || ln.qty_planned || 0
                  );
                  const delta = shipped - Number(r.reappro || 0);
                  const deltaColor =
                    delta > 0.001
                      ? "text-[#C05C47] font-semibold"
                      : delta < -0.001
                      ? "text-[#B48A00] font-semibold"
                      : "text-[#6B6E6B]";
                  const isLast = lastScannedKey === key;
                  return (
                    <tr
                      key={key}
                      className={`border-t border-[#E5E5E0] transition-colors ${
                        isLast ? "bg-[#EAF4EE]" : ""
                      }`}
                      data-testid={`onsite-row-${key}`}
                    >
                      <td className="px-2 py-2 font-mono font-semibold text-base">
                        {ln.slot_number || "—"}
                      </td>
                      <td className="px-2 py-2">
                        <div className="font-medium">
                          {ln.product_name || ln.product_id}
                        </div>
                        {ln.product_sku && (
                          <div className="text-[10px] text-[#6B6E6B] font-mono">
                            {ln.product_sku}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2 text-center font-mono font-semibold text-base">
                        {shipped}
                      </td>
                      <td className="px-1 py-1">
                        <Input
                          type="number"
                          step="1"
                          min="0"
                          inputMode="numeric"
                          value={r.contenu_reel}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) =>
                            setLine(ln, {
                              contenu_reel:
                                e.target.value === "" ? 0 : Number(e.target.value),
                            })
                          }
                          className="text-center h-11 font-mono text-base"
                          data-testid={`onsite-restant-${key}`}
                        />
                      </td>
                      <td className="px-1 py-1">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-9 w-9"
                            onClick={() => bumpLine(ln, "dlc_expired_qty", -1)}
                            data-testid={`onsite-dlc-minus-${key}`}
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                          <Input
                            type="number"
                            step="1"
                            min="0"
                            inputMode="numeric"
                            value={r.dlc_expired_qty}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) =>
                              setLine(ln, {
                                dlc_expired_qty:
                                  e.target.value === "" ? 0 : Number(e.target.value),
                              })
                            }
                            className="text-center h-11 font-mono text-base w-12"
                            data-testid={`onsite-dlc-${key}`}
                          />
                        </div>
                      </td>
                      <td className="px-1 py-1">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-11 w-11 border-[#C05C47]/40 text-[#C05C47]"
                            onClick={() => bumpLine(ln, "reappro", -1)}
                            data-testid={`onsite-reappro-minus-${key}`}
                          >
                            <Minus className="w-5 h-5" />
                          </Button>
                          <Input
                            type="number"
                            step="1"
                            min="0"
                            inputMode="numeric"
                            value={r.reappro}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) =>
                              setLine(ln, {
                                reappro:
                                  e.target.value === "" ? 0 : Number(e.target.value),
                              })
                            }
                            className="text-center h-11 font-mono text-base w-12"
                            data-testid={`onsite-reappro-${key}`}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-11 w-11 border-[#1F4E3D]/40 text-[#1F4E3D]"
                            onClick={() => bumpLine(ln, "reappro", 1)}
                            data-testid={`onsite-reappro-plus-${key}`}
                          >
                            <Plus className="w-5 h-5" />
                          </Button>
                        </div>
                      </td>
                      <td
                        className={`px-2 py-2 text-center font-mono text-base ${deltaColor}`}
                        data-testid={`onsite-delta-${key}`}
                      >
                        {Math.abs(delta) < 0.001
                          ? "0"
                          : delta > 0
                          ? `-${delta}`
                          : `+${-delta}`}
                      </td>
                      <td className="px-1 py-1">
                        <Input
                          type="text"
                          placeholder="Note…"
                          value={r.note}
                          onChange={(e) => setLine(ln, { note: e.target.value })}
                          className="h-10 text-xs"
                          data-testid={`onsite-note-${key}`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Header machine (heure, nettoyage, état) */}
          <div className="border-t border-[#E5E5E0] p-3 bg-[#FAFAF7] grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">
                Heure passage
              </label>
              <Input
                type="time"
                value={headerMap[currentGroup.distributor_id]?.heure_passage || ""}
                onChange={(e) =>
                  setHeader(currentGroup.distributor_id, "heure_passage", e.target.value)
                }
                className="h-10 font-mono mt-1"
                data-testid={`onsite-heure-${currentGroup.distributor_id}`}
              />
            </div>
            <div className="flex items-end gap-2 pb-1">
              <Checkbox
                id={`onsite-nettoye-${currentGroup.distributor_id}`}
                checked={Boolean(headerMap[currentGroup.distributor_id]?.nettoye)}
                onCheckedChange={(v) =>
                  setHeader(currentGroup.distributor_id, "nettoye", Boolean(v))
                }
                data-testid={`onsite-nettoye-${currentGroup.distributor_id}`}
              />
              <label
                htmlFor={`onsite-nettoye-${currentGroup.distributor_id}`}
                className="text-sm cursor-pointer select-none"
              >
                Machine nettoyée
              </label>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">
                État machine
              </label>
              <Input
                type="text"
                placeholder="OK / porte grince…"
                value={headerMap[currentGroup.distributor_id]?.etat_machine || ""}
                onChange={(e) =>
                  setHeader(currentGroup.distributor_id, "etat_machine", e.target.value)
                }
                className="h-10 text-xs mt-1"
                data-testid={`onsite-etat-${currentGroup.distributor_id}`}
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">
                Note générale
              </label>
              <Input
                type="text"
                placeholder="Commentaire…"
                value={headerMap[currentGroup.distributor_id]?.note_generale || ""}
                onChange={(e) =>
                  setHeader(currentGroup.distributor_id, "note_generale", e.target.value)
                }
                className="h-10 text-xs mt-1"
                data-testid={`onsite-note-generale-${currentGroup.distributor_id}`}
              />
            </div>
          </div>
        </div>
      )}

      {/* Sticky bottom CTA — bascule distrib + validation */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-[#E5E5E0] py-3 px-4 flex items-center gap-3 flex-wrap shadow-lg">
        <div className="text-xs text-[#6B6E6B]">
          {distribGroups.findIndex((g) => g.distributor_id === selectedDistrib) + 1}
          {" / "}
          {distribGroups.length} distrib
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            const idx = distribGroups.findIndex(
              (g) => g.distributor_id === selectedDistrib
            );
            if (idx > 0) setSelectedDistrib(distribGroups[idx - 1].distributor_id);
          }}
          disabled={
            distribGroups.findIndex((g) => g.distributor_id === selectedDistrib) <= 0
          }
          data-testid="onsite-prev-distrib"
        >
          ← Précédent
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            const idx = distribGroups.findIndex(
              (g) => g.distributor_id === selectedDistrib
            );
            if (idx < distribGroups.length - 1)
              setSelectedDistrib(distribGroups[idx + 1].distributor_id);
          }}
          disabled={
            distribGroups.findIndex((g) => g.distributor_id === selectedDistrib) >=
            distribGroups.length - 1
          }
          data-testid="onsite-next-distrib"
        >
          Suivant →
        </Button>
        <div className="flex-1" />
        <Button
          onClick={submit}
          disabled={submitting}
          className="h-11 bg-[#1F4E3D] hover:bg-[#173D2F]"
          data-testid="onsite-submit-btn"
        >
          {submitting ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <CheckCircle2 className="w-4 h-4 mr-2" />
          )}
          Valider & clôturer la tournée
        </Button>
      </div>

      {/* Scanner caméra */}
      <BarcodeScanner
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onDetected={handleCameraScan}
      />
    </div>
  );
}

function Stat({ label, value, color, warn }) {
  return (
    <div
      className="border p-2"
      style={{
        borderColor: warn ? color : "#E5E5E0",
        background: warn ? `${color}0F` : "white",
      }}
    >
      <div className="text-[9px] uppercase tracking-wider text-[#6B6E6B]">{label}</div>
      <div
        className="text-lg sm:text-xl font-mono font-semibold"
        style={{ color }}
      >
        {value}
      </div>
    </div>
  );
}
